import React, { useState } from 'react';
import { Waves, Wind, ArrowUp, ArrowDown, Clock, ChevronUp, ChevronDown, Droplets, Thermometer, Anchor } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatTideTime, DataSourceBadge, LoadingDots } from './constants';

export default function SpotTidesTab({ noaa, noaaLoaded, tabLoading }) {
  const [showAll, setShowAll] = useState(false);

  if (tabLoading.noaa && !noaaLoaded) {
    return <LoadingDots color="bg-blue-400" />;
  }

  if (!noaaLoaded) {
    return <p className="text-xs text-muted-foreground/50 text-center py-8">No NOAA tide data available for this location</p>;
  }

  const currentHr = new Date().getHours();
  const noaaHourly = noaa.hourly || [];
  const visible = showAll ? noaaHourly : noaaHourly.filter(h => { const hh = parseInt(h.hour?.split(':')[0] || '0', 10); return hh >= currentHr - 1; }).slice(0, 12);
  const nextTide = noaa.tides.find(t => { const d = new Date(t.time.replace(' ', 'T')); return d > new Date(); });

  return (
    <div className="space-y-4" data-testid="spot-tides">
      {/* Station info + summary cards */}
      <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Anchor className="w-3.5 h-3.5 text-blue-400/50" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">NOAA Tides</span>
              <DataSourceBadge label="NOAA" active />
            </div>
            <p className="text-[10px] text-muted-foreground/40 mt-1">{noaa.station?.name} ({noaa.station?.distance_km}km away)</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {nextTide && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
              <div className="flex items-center gap-1 mb-1">
                {nextTide.type === 'high' ? <ArrowUp className="w-3.5 h-3.5 text-blue-400" /> : <ArrowDown className="w-3.5 h-3.5 text-cyan-400" />}
                <span className="text-[9px] text-muted-foreground/50 uppercase">next {nextTide.type}</span>
              </div>
              <p className="text-sm font-bold">{nextTide.height_ft.toFixed(1)}<span className="text-[9px] text-muted-foreground/50">ft</span></p>
              <p className="text-[9px] text-muted-foreground/40">{formatTideTime(nextTide.time)}</p>
            </div>
          )}
          {noaa.water_level && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Droplets className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[9px] text-muted-foreground/50 uppercase">water level</span>
              </div>
              <p className="text-sm font-bold">{noaa.water_level.level_ft.toFixed(1)}<span className="text-[9px] text-muted-foreground/50">ft</span></p>
              <p className="text-[9px] text-muted-foreground/40">observed</p>
            </div>
          )}
          {noaa.wind && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Wind className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-[9px] text-muted-foreground/50 uppercase">wind</span>
              </div>
              <p className="text-sm font-bold">{noaa.wind.speed_kts}<span className="text-[9px] text-muted-foreground/50">kts {noaa.wind.direction}</span></p>
              <p className="text-[9px] text-muted-foreground/40">gusts {noaa.wind.gust_kts}kts</p>
            </div>
          )}
          {noaa.water_temp && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Thermometer className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-[9px] text-muted-foreground/50 uppercase">water temp</span>
              </div>
              <p className="text-sm font-bold">{noaa.water_temp.temp_f}<span className="text-[9px] text-muted-foreground/50">&deg;F</span></p>
              <p className="text-[9px] text-muted-foreground/40">{formatTideTime(noaa.water_temp.time)}</p>
            </div>
          )}
        </div>
      </div>

      {/* High/Low tides */}
      {noaa.tides.length > 0 && (
        <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4" data-testid="hilo-tides">
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-3.5 h-3.5 text-blue-400/50" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Today's Tides</span>
          </div>
          <div className="space-y-2">
            {noaa.tides.map((t, i) => {
              const isPast = new Date(t.time.replace(' ', 'T')) < new Date();
              return (
                <div key={i} className={`flex items-center gap-3 p-2.5 rounded-xl border ${isPast ? 'bg-white/[0.01] border-white/5 opacity-50' : t.type === 'high' ? 'bg-blue-400/5 border-blue-400/15' : 'bg-cyan-400/5 border-cyan-400/15'}`} data-testid={`tide-event-${i}`}>
                  {t.type === 'high' ? <ArrowUp className="w-4 h-4 text-blue-400 flex-shrink-0" /> : <ArrowDown className="w-4 h-4 text-cyan-400 flex-shrink-0" />}
                  <div className="flex-1"><span className={`text-xs font-bold ${t.type === 'high' ? 'text-blue-400' : 'text-cyan-400'}`}>{t.type === 'high' ? 'High' : 'Low'} Tide</span></div>
                  <div className="text-right">
                    <p className="text-xs font-bold">{t.height_ft.toFixed(1)} ft</p>
                    <p className="text-[9px] text-muted-foreground/50">{formatTideTime(t.time)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hourly tide bars */}
      {visible.length > 0 && (
        <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4" data-testid="hourly-tides">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-blue-400/50" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Hourly Tide Level</span>
            </div>
            <button data-testid="toggle-tide-hours" onClick={() => setShowAll(!showAll)}
              className="text-[10px] text-blue-400/60 hover:text-blue-400 flex items-center gap-0.5">
              {showAll ? 'current' : 'all 24h'}
              {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          <div className="space-y-1">
            {visible.map((h, i) => {
              const pct = Math.max(5, Math.min(100, ((h.height_ft + 2) / 8) * 100));
              return (
                <div key={i} className="flex items-end gap-1.5" data-testid={`tide-bar-${h.hour}`}>
                  <span className="text-[9px] text-muted-foreground/50 w-10 text-right font-mono flex-shrink-0">{h.hour || '?'}</span>
                  <div className="flex-1 h-5 bg-white/[0.03] rounded-full overflow-hidden relative">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }}
                      className={`h-full rounded-full ${h.height_ft > 4 ? 'bg-blue-400/40' : h.height_ft > 2 ? 'bg-primary/30' : 'bg-cyan-400/20'}`} />
                  </div>
                  <span className="text-[10px] font-bold w-10 flex-shrink-0">{h.height_ft.toFixed(1)}<span className="text-[8px] text-muted-foreground/40">ft</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
